/**
 * Pill / Badge — Sprint-16-Brand.
 *
 * Rounded-full Chip. Zwei Rollen unter einer Component:
 *   1. STATUS-Badge (uppercase, tracking-wider) — Live · Stop-Sell · Achtung
 *   2. NUMERIC-Pill (font-mono, tight tracking) — Rate-Numeric, Nights-Count
 *
 * Design-System 4h_2 Sprint-16-Varianten:
 *   - live:     Sage-Soft + Sage-Text (● Live, ● Aktiv, ● Verbunden)
 *   - stop:     Sealwax-Soft + Sealwax-Text (Stop-Sell, Blocked, Trennen)
 *   - warning:  Amber-Soft + Amber-Dark (Drift, Out-of-Sync, Achtung)
 *   - numeric:  Card-Alt + Ink + Mono (140 €/Nacht, +2 Gäste)
 *
 * Legacy-Compat (Sprint 1-15 Konsumer):
 *   - accent → sage-soft (alias to live-Style)
 *   - error → sealwax-soft (alias to stop-Style)
 *   - default, on, outline bleiben strukturell erhalten, neue Palette
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const pillVariants = cva(
  ['inline-flex items-center gap-1.5', 'rounded-full font-sans', 'whitespace-nowrap select-none'],
  {
    variants: {
      variant: {
        // Sprint-16-Kern
        live: 'bg-sage/15 text-sage font-bold uppercase tracking-[0.08em]',
        stop: 'bg-sealwax/15 text-sealwax font-bold uppercase tracking-[0.08em]',
        warning: 'bg-amber/15 text-amber font-bold uppercase tracking-[0.08em]',
        numeric: 'bg-card-alt text-ink font-mono tracking-tight',

        // Neutral / Legacy
        default: 'bg-card-alt border border-line text-ink-soft',
        on: 'bg-navy text-paper',
        outline: 'bg-transparent border border-ink-faint text-ink-soft',

        // Legacy-Aliase (Sprint 1-15 Konsumer)
        accent: 'bg-sage/15 text-sage font-bold uppercase tracking-[0.08em]',
        error: 'bg-sealwax/15 text-sealwax font-bold uppercase tracking-[0.08em]',
      },
      size: {
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-2.5 py-0.5 text-[11.5px]',
        lg: 'px-3 py-1 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface PillProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof pillVariants> {}

export const Pill = forwardRef<HTMLSpanElement, PillProps>(
  ({ className, variant, size, ...props }, ref) => (
    <span ref={ref} className={cn(pillVariants({ variant, size }), className)} {...props} />
  ),
);
Pill.displayName = 'Pill';

export { pillVariants };
